import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';

import { Observable, Subscription, filter, map, take, throttleTime } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { GtmService } from '@fcom/common/gtm';
import { NavigatedPathToContentWrapperService, NavigationMenuService, PageMetaService } from '@fcom/common/services';
import { TimeAndGeoipProvider, CookieService } from '@fcom/core/services';
import { unsubscribe, urlQueryString, urlToPathname } from '@fcom/core/utils';
import { PersonalizationTrackingService } from '@fcom/loyalty-personalization/services';
import { DataCloudService } from '@fcom/common/datacloud';
import { LoginIframeComponent } from '@fcom/common/login';
import { MultivariateTestService } from '@fcom/common/multivariate-test/services/multivariate-test.service';
import { Cms3DSeatMapService } from '@fcom/cms/services';
import { isNavigationEvent } from '@fcom/common/utils';
import { UTM_PARAMS } from '@fcom/core/constants';
import { GtmEvent } from '@fcom/common/interfaces';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
export const AW_REF_COOKIE = 'aw_ref';

@Component({
  selector: 'fin-app',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements AfterViewInit, OnDestroy, OnInit {
  private navigationMenuService = inject(NavigationMenuService);
  private timeAndGeoipProvider = inject(TimeAndGeoipProvider);
  private pageMetaService = inject(PageMetaService);
  private router = inject(Router);
  private renderer = inject(Renderer2);
  private languageService = inject(LanguageService);
  private dataLayerService = inject(GtmService);
  private dataCloudService = inject(DataCloudService);
  private multiVariateTestService = inject(MultivariateTestService);
  private cms3DSeatMapService = inject(Cms3DSeatMapService);
  private navigatedPathToContentWrapperService = inject(NavigatedPathToContentWrapperService);
  private personalizationTrackingService = inject(PersonalizationTrackingService);
  private cookieService = inject(CookieService);
  private platform = inject(PLATFORM_ID);
  private finnairThemeService = inject(FinnairThemeService);

  isNavigationMenuOpen$: Observable<boolean>;
  title$: Observable<string>;
  removeGlobalListener: () => void;
  contentWrapperNavigatedClass$: Observable<string>;
  public readonly LOGIN_IFRAME_COMPONENT_TYPE = LoginIframeComponent;

  private subscriptions: Subscription = new Subscription();

  @ViewChild('contentWrapper', { static: true })
  contentWrapper: ElementRef;

  constructor() {
    this.isNavigationMenuOpen$ = this.navigationMenuService.isNavigationMenuOpen$;
    this.title$ = this.pageMetaService.title$;

    this.subscriptions.add(
      this.languageService.lang.subscribe((l) => {
        this.pageMetaService.setDocumentLanguage(l, this.renderer);
      })
    );

    this.finnairThemeService.applyThemeClass();

    this.contentWrapperNavigatedClass$ = this.navigatedPathToContentWrapperService.contentWrapperNavigatedClass$;
  }

  ngOnInit(): void {
    this.pushNavigationEventsToDataLayer();
    this.checkForOriginalSource();
    this.navigatedPathToContentWrapperService.setRouter(this.router);
    this.multiVariateTestService.init();
    this.personalizationTrackingService.init();
  }

  ngAfterViewInit(): void {
    this.timeAndGeoipProvider.triggerTimeAndGeoipFetch();

    this.globalClicksHandler();
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);

    if (this.removeGlobalListener) {
      this.removeGlobalListener();
    }
  }

  private checkForOriginalSource(): void {
    if (!this.cookieService.hasItem(AW_REF_COOKIE)) {
      return;
    }

    this.subscriptions.add(
      this.router.events.pipe(filter(isNavigationEvent), take(1)).subscribe((event: NavigationEnd) => {
        if (event.url.includes('awc')) {
          return;
        }

        const trafficSource = this.determineTrafficSource(event.url);
        if (trafficSource) {
          const expirationDate = new Date();
          expirationDate.setDate(expirationDate.getDate() + 30);
          this.cookieService.setItem(AW_REF_COOKIE, trafficSource, expirationDate, '/', 'finnair.com', true, 'lax');
          this.dataLayerService.pushEventToDataLayer(GtmEvent.AW_NEW_SOURCE);
        }
      })
    );
  }

  private determineTrafficSource(url: string): 'campaign' | 'referral' | null {
    // Check for UTM parameters in URL - campaign source
    const hasUtmParams = UTM_PARAMS.some((param) => url.includes(`${param}=`));
    if (hasUtmParams) {
      return 'campaign';
    }

    // Check for external referrer - referral source
    const hasExternalReferrer = document.referrer && !document.referrer.includes('finnair.com');
    if (hasExternalReferrer) {
      return 'referral';
    }

    return null;
  }

  /**
   * The throttle is implemented to prevent duplication of page view tracking events when app is handling entrypoint urls that contain query params.
   * In these cases 2 navigationEnd events are fired in short time, one for initial landing and one after the params have been stripped out.
   * Throttle was chosen instead of debounce time to capture query params of the initial event.
   * 200ms was chosen as it is short enough not to harm natural navigation behavior, but long enough to cover this case.
   */
  private pushNavigationEventsToDataLayer() {
    if (isPlatformBrowser(this.platform)) {
      this.subscriptions.add(
        this.router.events.pipe(filter(isNavigationEvent), take(1)).subscribe((event: NavigationEnd) => {
          this.dataLayerService.originalPageView(event.urlAfterRedirects);
        })
      );

      this.subscriptions.add(
        this.router.events.pipe(filter(isNavigationEvent), throttleTime(200)).subscribe((event: NavigationEnd) => {
          this.dataLayerService.pageView(event.urlAfterRedirects);
        })
      );

      this.subscriptions.add(
        this.router.events
          .pipe(
            filter(isNavigationEvent),
            map((event) => new URLSearchParams(urlQueryString(event.urlAfterRedirects)).get('sfmc_sub')),
            filter(Boolean),
            take(1)
          )
          .subscribe((subscriberId: string) => {
            this.dataCloudService.subscriberId(subscriberId);
          })
      );
    }
  }

  private globalClicksHandler() {
    this.removeGlobalListener = this.renderer.listen('document', 'click', (event) => {
      const target = event.target;

      const link = (target as Element).closest('a');
      const url: string = link?.href;

      if (link && url?.includes?.('#3dseatmap')) {
        event.preventDefault();
        event.stopPropagation();
        this.seatMapLinkListener(url);
        return;
      }

      if (
        target &&
        typeof target.tagName === 'string' &&
        target.tagName.toLowerCase() === 'a' &&
        target.target !== '_blank' &&
        target.getAttribute('data-contentid')
      ) {
        // Application internal URL, use the router to navigate
        this.router.navigateByUrl(urlToPathname(target.href));
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    });
  }

  private seatMapLinkListener(url: string): void {
    const parts = url.split('/');
    const cabinClass = parts.pop();
    const fleet = parts.pop();
    this.cms3DSeatMapService.open3dSeatMap(fleet, cabinClass);
  }
}
